import { useState } from "react";
import { SendHorizontal } from "lucide-react";
import {
  useCreate,
  useGetIdentity,
  useGetList,
  useNotify,
  useTranslate,
  type Identifier,
} from "ra-core";

import { Textarea } from "@/components/ui/textarea";
import type { ContentComment } from "../types";

export const PieceComments = ({
  pieceId,
  authorKind = "team",
  authorName,
}: {
  pieceId: Identifier;
  authorKind?: "team" | "client";
  /** Overrides the staff identity name (used by the client portal). */
  authorName?: string;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create, { isPending }] = useCreate<ContentComment>();
  const [body, setBody] = useState("");
  const { data: comments = [] } = useGetList<ContentComment>(
    "content_comments",
    {
      filter: { piece_id: pieceId },
      sort: { field: "created_at", order: "ASC" },
      pagination: { page: 1, perPage: 200 },
    },
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const text = body.trim();
    if (!text) return;
    create(
      "content_comments",
      {
        data: {
          piece_id: pieceId,
          body: text,
          author_kind: authorKind,
          author_name: authorName ?? identity?.fullName ?? "CMG360",
        },
      },
      {
        onSuccess: () => setBody(""),
        onError: () => notify("ra.notification.http_error", { type: "error" }),
      },
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
        {translate("resources.content_pieces.comments.title")}
      </span>
      {comments.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.content_pieces.comments.empty")}
        </p>
      ) : (
        <ul className="flex flex-col gap-2.5">
          {comments.map((comment) => (
            <li key={comment.id} className="flex gap-2.5">
              <span
                className={`grid size-7 shrink-0 place-items-center rounded-full text-[11px] font-bold ${
                  comment.author_kind === "client"
                    ? "bg-[#f3d9c4] text-[#5a3416]"
                    : "bg-primary/20 text-primary"
                }`}
              >
                {comment.author_name.slice(0, 2).toUpperCase()}
              </span>
              <div className="min-w-0 rounded-[4px_14px_14px_14px] bg-secondary px-3 py-2 text-sm leading-relaxed">
                <span className="mb-0.5 block text-[11px] text-muted-foreground">
                  {comment.author_name}
                  {comment.author_kind === "client"
                    ? ` · ${translate("resources.content_pieces.comments.client")}`
                    : ""}
                  {" · "}
                  {new Date(comment.created_at).toLocaleString(undefined, {
                    day: "numeric",
                    month: "short",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
                <span className="whitespace-pre-line">{comment.body}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={handleSubmit} className="flex items-end gap-2">
        <Textarea
          rows={2}
          value={body}
          placeholder={translate(
            "resources.content_pieces.comments.placeholder",
          )}
          onChange={(event) => setBody(event.target.value)}
          className="min-h-0 resize-none"
        />
        <button
          type="submit"
          disabled={isPending || !body.trim()}
          aria-label={translate("resources.content_pieces.comments.send")}
          className="grid size-10 shrink-0 place-items-center rounded-full bg-primary text-primary-foreground disabled:opacity-40"
        >
          <SendHorizontal className="size-4" />
        </button>
      </form>
    </div>
  );
};
